import { useMemo } from 'react';

import { useMy } from '@/hooks';
import { useGetCollaboratorsListFromAccounts } from '@/hooks/useAccounts';
import { useCustomerType } from '@/hooks/useCustomerType';
import { defaultFilters } from '@/sections/customerManagement/config/columnDefinitions';
import {
  useCustomerIntegrationHealth,
  useCustomerRiskCounts,
} from '@/sections/customerManagement/hooks/data';
import { useBulkCustomerStatus } from '@/sections/customerManagement/hooks/status';
import { useCustomerDisplayName } from '@/sections/customerManagement/hooks/useCustomerDisplayName';
import {
  CustomerDataWithMetrics,
  Filters,
  Status,
} from '@/sections/customerManagement/types';
import { CustomerTypeValue, RiskSeverity } from '@/types';
import { mergeStatus } from '@/utils/api';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

const isValidStatus = (status: unknown): status is Status => {
  return (
    typeof status === 'string' &&
    ['Active', 'Paused', 'Setup', 'Upcoming', 'Completed'].includes(status)
  );
};

export const useCustomerManagement = () => {
  const [filters, setFilters] = useStorage<Filters>(
    {
      queryKey: 'customerManagementFilters.v3',
      key: StorageKey.FILTERS_CUSTOMER_MANAGEMENT,
    },
    defaultFilters
  );

  const { data: accounts, status: accountsStatus } = useMy(
    {
      resource: 'account',
    },
    {
      doNotImpersonate: true, // Customer management should always run as the actual user
    }
  );
  const collaborators = useGetCollaboratorsListFromAccounts(accounts);
  const typeQuery = useCustomerType(true); // doNotImpersonate for customer management
  const customerStatusQuery = useBulkCustomerStatus(
    collaborators.map(c => c.name),
    typeQuery.dataMap as Map<string, CustomerTypeValue>
  );
  const riskCountsQuery = useCustomerRiskCounts();
  const displayNameQuery = useCustomerDisplayName();
  const integrationHealthQuery = useCustomerIntegrationHealth();

  const customers = useMemo<CustomerDataWithMetrics[]>(() => {
    return collaborators.map(collaborator => {
      const email = collaborator.name;
      const healthData = integrationHealthQuery.dataMap.get(email);
      const rawStatus = customerStatusQuery.data.get(email) || 'Setup';
      const resolvedDisplayName = displayNameQuery.dataMap.get(email) || email;

      return {
        email,
        displayName: resolvedDisplayName,
        status: isValidStatus(rawStatus) ? rawStatus : 'Setup',
        type: typeQuery.dataMap.get(email) || undefined,
        critical:
          riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Critical}`) || 0,
        high: riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.High}`) || 0,
        medium:
          riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Medium}`) || 0,
        low: riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Low}`) || 0,
        info: riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Info}`) || 0,
        exposure: 0,
        healthyIntegrations: healthData?.healthyIntegrations || 0,
        totalIntegrations: healthData?.totalIntegrations || 0,
        health: healthData
          ? `${healthData.healthyIntegrations}/${healthData.totalIntegrations}`
          : '',
        riskCountsStatus: riskCountsQuery.status || 'pending',
        typeStatus: typeQuery.status || 'pending',
        integrationHealthStatus: integrationHealthQuery?.status || 'pending',
        statusStatus: customerStatusQuery.status || 'pending',
        queryStatus: {
          riskCounts: riskCountsQuery.status || 'pending',
          integrationHealth: integrationHealthQuery?.status || 'pending',
          type: typeQuery.status || 'pending',
          status: customerStatusQuery.status || 'pending',
        },
      };
    });
  }, [
    collaborators,
    customerStatusQuery,
    riskCountsQuery,
    integrationHealthQuery,
    typeQuery,
    displayNameQuery,
  ]);

  const filteredCustomers = useMemo(() => {
    let filteredCollaborators = customers;
    if (filters.search) {
      filteredCollaborators = customers.filter(customer => {
        return (
          customer.displayName
            .toLowerCase()
            .includes(filters.search!.toLowerCase()) ||
          customer.email.toLowerCase().includes(filters.search!.toLowerCase())
        );
      });
    }
    if (filters.advancedFilters && filters.advancedFilters.status) {
      filteredCollaborators = filteredCollaborators.filter(customer => {
        return filters.advancedFilters!.status!.includes(customer.status);
      });
    }
    if (filters.advancedFilters && filters.advancedFilters.type) {
      filteredCollaborators = filteredCollaborators.filter(customer => {
        return customer.type && typeof customer.type === 'string'
          ? filters.advancedFilters!.type!.includes(customer.type)
          : false;
      });
    }
    return filteredCollaborators;
  }, [customers, filters]);

  return {
    filters,
    setFilters,
    customers: filteredCustomers,
    allCustomers: customers, // Raw unfiltered customer list
    customersStatus: mergeStatus(
      accountsStatus,
      // customerStatusQuery.status,
      displayNameQuery.status
    ),
    totalCustomers: collaborators.length,
  };
};
